import type { ActiveDiscountOffer } from '../types/discount-offer';

export type DiscountOptionKey = 'pay_now' | 'payment_plan';

/**
 * Apply the campaign's pay_now / payment_plan discount to whatever the customer
 * currently owes. The offer snapshot freezes the percentage + caps at launch
 * time, but the balance moves — so we always recompute against the current
 * balance rather than any snapshotted "original" amount.
 *
 * Returns null when the option is disabled, the offer is past its expiry, or
 * the campaign hasn't started yet.
 */
export function discountedAmountMillicents(
  offer: ActiveDiscountOffer,
  optionKey: DiscountOptionKey,
  currentAmountMillicents: number
): number | null {
  if (!isOfferActive(offer)) return null;
  const opt = offer.configSnapshot.options?.[optionKey];
  if (!opt?.enabled) return null;
  const raw = Math.floor(currentAmountMillicents * opt.percentage);
  const capped = opt.max_off_millicents == null ? raw : Math.min(raw, opt.max_off_millicents);
  return Math.max(0, currentAmountMillicents - capped);
}

/**
 * Discount amount (positive) that would be applied to the current balance for
 * a given option, or null when the option is disabled / offer inactive.
 */
export function discountSavingsMillicents(
  offer: ActiveDiscountOffer,
  optionKey: DiscountOptionKey,
  currentAmountMillicents: number
): number | null {
  const discounted = discountedAmountMillicents(offer, optionKey, currentAmountMillicents);
  if (discounted == null) return null;
  return Math.max(0, currentAmountMillicents - discounted);
}

/**
 * True when the offer is currently usable: not expired/superseded by the
 * server, not past its client-side expiry, and the campaign has started.
 */
export function isOfferActive(offer: ActiveDiscountOffer, now: Date = new Date()): boolean {
  if (offer.status !== 'eligible' && offer.status !== 'accepted') return false;
  if (offer.expiresAt && new Date(offer.expiresAt).getTime() <= now.getTime()) return false;
  const startsAt = offer.configSnapshot.starts_at;
  if (startsAt && new Date(startsAt).getTime() > now.getTime()) return false;
  return true;
}
